import ko from 'knockout';
import moment, { isDuration } from 'moment';
import numeral from 'numeral';
import { sum } from 'lodash';
import { customFetch as fetch, CustomError } from "../../fetch";
import { formatHours as _formatHours } from '../../hours-utils';
import { closePopups, openPopup } from '../../init';
import { errorToast, successToast } from '../../toastr-helper';
import SelectListItemModel from '../../custom/selectlist-item-model';
import { groupBy } from 'lodash';

declare const areaName: string;
// asp.net view model
declare const viewModel: any;

export class OtherHoursInfoModel {
    tag: KnockoutObservable<string>;
    name: KnockoutObservable<string>;
    total: KnockoutObservable<moment.Duration>;

    constructor(model: any) {
        this.tag = ko.observable(model.tag || '');
        this.name = ko.observable(model.name || '');
        this.total = ko.observable(model.total ?
            moment.duration(model.total, 'hours') : null);
    }
}

export class DailyOverviewModel {
    readonly self: DailyOverviewModel = this;

    // Observables
    employeeId: KnockoutObservable<string>;
    dailyData: KnockoutObservable<DayHoursModel[]>;

    otherHoursInfo: KnockoutComputed<OtherHoursInfoModel[]>;

    constructor(model: any = {}) {
        this.dailyData = ko.observable(model.dailyData || []);
        this.otherHoursInfo = ko.pureComputed(() => {
            if (this.dailyData().length == 0) {
                return [];
            }

            const flattenedOtherHours: any[] = [];

            this.dailyData()
                .forEach(day => day.otherHours()
                    .forEach(item => flattenedOtherHours.push(item)));

            const tagHours = groupBy(flattenedOtherHours, 'tag');
            const infos = Object.keys(tagHours)
                .map(key => new OtherHoursInfoModel({
                    tag: key,
                    name: tagHours[key][0].name, // they're already grouped, so the first is as good as any other
                    total: tagHours[key].reduce((sum: number, item) => sum + item.hours, 0),
                }));

            return infos;
        });
    }

    formatHours(hours: any, format: string = 'HH:mm') {
        const value = ko.unwrap(hours);
        return _formatHours(value, format);
    }

    /**
     * Compute a simple sum on the monthly data. The items to be summed
     * over are retrieved through the given selector function.
     *
     * @param selector the selector function to extract the item to be summed over.
     */
    protected computeTotal(selector: (item: DayHoursModel) => moment.Duration | number): moment.Duration {
        return this.dailyData().reduce((total, day) => {
            let hours = selector(day);
            if (!isDuration(hours))
                hours = moment.duration(hours, 'hours');

            return total.add(hours);
        }, moment.duration(0));
    }

    reload() {
        this.dailyData.valueHasMutated!();
    }
}

export class EditMultipleOtherHoursModel {
    private readonly employeeId: string;
    private readonly availableHoursTypes: any[];
    private readonly saveCallback: () => void;

    records: KnockoutObservable<EditOtherHoursModel[]>;
    selectedRecord: KnockoutObservable<EditOtherHoursModel>;

    saving: KnockoutObservable<boolean>;
    editing: KnockoutObservable<boolean>;
    confirmDelete: KnockoutObservable<boolean>;

    constructor(employeeId: string, availableHoursTypes: any[], saveCallback: () => void) {
        this.employeeId = employeeId;
        this.availableHoursTypes = availableHoursTypes;
        this.saveCallback = saveCallback;

        this.records = ko.observable([]);
        this.selectedRecord = ko.observable<EditOtherHoursModel>(null);

        this.saving = ko.observable(false);
        this.editing = ko.observable(false);
        this.confirmDelete = ko.observable(false);
    }

    selectRecord(record: EditOtherHoursModel) {
        this.selectedRecord(record);
    }

    edit(records: any[], period = {} ) {
        const mappedRecords = records.map(rec =>
            new EditOtherHoursModel(
                this.employeeId,
                { ...rec,
                    availableHoursTypes: this.availableHoursTypes,
                    availableDates: period
                }));

        this.records(mappedRecords);
        this.selectedRecord(mappedRecords[0]);

        this.editing(true);
    }

    close() {
        this.editing(false);
    }

    showConfirmDelete() {
        this.confirmDelete(true);
    }

    async submit() {
        this.saving(true);

        let error = false;
        for (let record of this.records()) {
            if (!await record.submit()) {
                this.selectRecord(record);
                error = true;

                break;
            };
        }

        if (error) {
            errorToast('Er ging iets mis tijden het opslaan van de uren. Controleer de gegevens op fout.');
        }
        else {
            this.saveCallback();
            this.close();

            successToast('De uren zijn opegeslagen.');
        }

        this.saving(false);
    }

    async deleteHours() {
        this.saving(true);

        const idToDelete = this.selectedRecord().id();
        const response = await fetch(`/${areaName}/Employee/Hours/Delete/${idToDelete}`, {
            method: 'DELETE'
        });

        this.saving(false);
        this.confirmDelete(false);

        if (response.ok) {
            successToast('Het item is verwijderd')
            this.records(this.records()
                .filter(rec => rec.id() !== idToDelete));

            if (this.records().length > 0) {
                this.selectRecord(this.records()[0]);
            }
            else {
                this.saveCallback();
                this.close();
            }
        }
        else if (response.errors) {
            errorToast('Er ging iets mis tijdens het verwijderen van het item');
            response.errors.forEach(err => console.error(err.message));
        }
    }

    cancel() {
        this.confirmDelete(false);
    }
}

export class EditOtherHoursModel {
    readonly availableDates: { from: moment.Moment, to: moment.Moment };

    validationErrors: KnockoutObservableArray<CustomError>;
    saving: KnockoutObservable<boolean>;

    id: KnockoutObservable<string>;
    employeeId: KnockoutObservable<string>;

    date: KnockoutObservable<moment.Moment>;
    availableTypes: KnockoutObservable<SelectListItemModel[]>;
    selectedType: KnockoutObservable<string>;
    timeMs: KnockoutObservable<number>;
    reason: KnockoutObservable<string>;
    name: KnockoutComputed<string>;

    formattedHours: KnockoutComputed<string>;
    formattedDate: KnockoutComputed<string>;

    isNew: KnockoutComputed<boolean>;

    constructor(employeeId: string, model: any = {}) {
        this.availableDates = model.availableDates;

        this.validationErrors = ko.observableArray([]);
        this.saving = ko.observable(false);

        this.id = ko.observable<string>(model.id)
        this.employeeId = ko.observable(employeeId);
        this.isNew = ko.pureComputed(() => this.id() == null);

        this.date = ko.observable(model.date ? moment(model.date) : moment())
            .extend({ errorHandler: { source: this.validationErrors, id: 'Date' } });

        this.formattedDate = ko.pureComputed(() => this.date().format('DD-MM-YYYY'));

        this.availableTypes = ko.observable(model.availableHoursTypes
            .map((type: any) => new SelectListItemModel(type)) || []);

        this.selectedType = ko.observable(model.hoursTypeId || null)
            .extend({ errorHandler: { source: this.validationErrors, id: 'HoursTypeId' } });;

        this.name = ko.pureComputed(() => {
            const type = this.availableTypes().find(type => type.id() == this.selectedType());
            return type ? type.name() : '';
        });

        this.timeMs = ko.observable(model.timeMs || 0)
            .extend({ errorHandler: { source: this.validationErrors, id: 'timeMs' } });

        this.formattedHours = ko.pureComputed(() => moment.duration(this.timeMs(), 'ms').format('HH:mm', {
            trim: false,
            trunc: true
        }));

        this.reason = ko.observable(model.reason || '');
    }

    datesRange() {
        const from = (this.availableDates.from || moment(this.date()).startOf('isoWeek'));
        const to = (this.availableDates.to || moment(this.date()).endOf('isoWeek'));

        return [{
            from: from.format('DD-MM-YYYY'),
            to: to.format('DD-MM-YYYY')
        }];
    }

    async submit() {
        const payload = {
            id: this.id(),
            employeeId: this.employeeId(),
            hoursTypeId: this.selectedType(),
            date: this.date() && this.date().format(),
            timeMs: moment.duration(this.timeMs()).asMilliseconds().toString(),
            reason: this.reason()
        }

        this.saving(true);

        const url = this.id() ?
            `/${areaName}/Employee/Hours/Edit` : `/${areaName}/Employee/Hours/Add`;

        const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        this.saving(false);

        if (response.errors) {
            this.validationErrors(response.errors);
        }

        return response.ok;
    }

    reset() {
        this.timeMs(0);
        this.date(moment());
        this.reason('');
        this.selectedType(null);
        this.validationErrors([]);
    }

    private update(model: any) {
        this.id(model.id || null)
        this.timeMs(moment.duration(model.hours || 0, 'hours').asMilliseconds());
        this.date(model.date || null);
        this.reason(model.reason || null);
        this.selectedType(model.hoursTypeId || null);
    }

    show(model: any = null) {
        this.reset();

        if (model)
            this.update(model);

        openPopup('edit-other-hours');
    }

    close() {
        closePopups();
    }
}

/**
 * Generic model for hours in a period. A reference date
 * is required for sorting and display purposes.
 *
 * @class HoursModel
 */
export class HoursModel {
    // Observables
    date: KnockoutObservable<moment.Moment>;
    contractHours: KnockoutObservable<moment.Duration>;
    clockedHours: KnockoutObservable<moment.Duration>;
    vacationHours: KnockoutObservable<any>;
    specialLeaveHours: KnockoutObservable<any>;
    sickLeaveHours: KnockoutObservable<any>;
    otherHours: KnockoutObservable<any[]>;
    aggregateOtherHours: KnockoutObservable<any[]>;

    // Computed observables
    periodTotal: KnockoutComputed<moment.Duration>;
    formattedVacationHours: KnockoutComputed<string>;
    formattedSpecialLeaveHours: KnockoutComputed<string>;
    formattedSickLeaveHours: KnockoutComputed<string>;
    hasData: KnockoutComputed<boolean>;

    otherHoursTotal: KnockoutComputed<moment.Duration>;
    aggregateOtherHoursTotal: KnockoutComputed<moment.Duration>;
    otherHoursGrandTotal: KnockoutComputed<moment.Duration>;

    formattedAggregateOtherHoursTotal: KnockoutComputed<string>;
    aggregateOtherHoursTooltip: KnockoutComputed<string>;

    constructor(model: any = {}) {
        this.date = ko.observable(moment(model.date));
        this.contractHours = ko.observable(moment.duration(model.contractHours, 'hours'));
        this.clockedHours = ko.observable(moment.duration(model.clockedHours, 'hours'));
        this.vacationHours = ko.observable(model.vacationHours);
        this.specialLeaveHours = ko.observable(model.specialLeaveHours);
        this.sickLeaveHours = ko.observable(model.sickLeaveHours);
        this.otherHours = ko.observable(model.otherHours ||
            model.components.reduce((array: any[], c: any) =>
                array.concat(c.otherHours), []));

        this.aggregateOtherHours = ko.observable(model.aggregateOtherHours ||
            model.components.reduce((array: any[], c: any) =>
                array.concat(c.aggregateOtherHours), []));

        this.periodTotal = ko.pureComputed(() => {
            return moment.duration(this.clockedHours())
                .add(this.vacationHours().hours, 'hours')
                .add(this.specialLeaveHours().hours, 'hours')
                .add(this.sickLeaveHours().hours, 'hours')
                .add(this.otherHoursGrandTotal())
                .subtract(this.contractHours());
        });

        this.hasData = ko.pureComputed(() =>
            this.contractHours().asMilliseconds() > 0 ||
            this.clockedHours().asMilliseconds() > 0 ||
            this.vacationHours().totalHours > 0 ||
            this.specialLeaveHours().totalHours > 0 ||
            this.sickLeaveHours().totalHours > 0 ||
            this.otherHours().some((item: any) => item.hours > 0) ||
            this.aggregateOtherHours().some((item: any) => item.hours > 0));

        this.otherHoursTotal = ko.pureComputed(() =>
            moment.duration(this.otherHours()
                .reduce((total: number, item: any) => total + (item.hours * item.sign), 0),
                'hours'));

        this.aggregateOtherHoursTotal = ko.pureComputed(() =>
            moment.duration(this.aggregateOtherHours()
                .reduce((total: number, item: any) => total + (item.hours * item.sign), 0),
                'hours'));

        this.otherHoursGrandTotal = ko.pureComputed(() =>
            this.otherHoursTotal().add(this.aggregateOtherHoursTotal()));

        this.aggregateOtherHoursTooltip = ko.pureComputed(() => {
            const content = this.aggregateOtherHours()
                .reduce((fullString, item) => {
                    const row = `<tr>
                            <td>${item.name}</td>
                            <td style="padding-left: 2rem;">
                                ${numeral(item.hours * item.sign).format('+#0.00')}
                            </td>
                            <td style="padding-left: 2rem;">${item.reason || 'geen'}</td>
                        </tr>`;
                    return `${fullString}${row}`;
                }, '');

            return `<table>${content}</table>`;

        });

        this.formattedAggregateOtherHoursTotal = ko.pureComputed(() => {
            if (this.aggregateOtherHoursTotal().asMilliseconds() == 0) {
                return this.aggregateOtherHours().length > 0 ?
                    this.aggregateOtherHoursTotal().format('HH:mm', { trim: false }) : '';
            }

            return this.formatHours(this.aggregateOtherHoursTotal);
        })
    }

    formatHours(hours: any, format: string = 'HH:mm') {
        const value = ko.unwrap(hours);
        return _formatHours(value, format);
    }

    /**
     * Always print the total hours for the record, prefixed with
     * a placeholder if requested.
     *
     * @param leave
     * @param placeholder
     */
    formatLeaveHours(leave: any, placeholder: string = '') {
        const value = ko.unwrap(leave);
        if (value.totalHours > 0) {
            // Always show the total for the record
            return placeholder ?
                `${placeholder} (${this.formatHours(value.totalHours)})` : `${this.formatHours(value.totalHours)}`;
        }

        return '';
    }

    hoursForTag(info: OtherHoursInfoModel) {
        const item = this.otherHours().find(item => item.tag === info.tag());
        if (item) {
            return moment.duration(item.hours, 'hours');
        }

        return moment.duration(0);
    }

    tooltipForTag(info: OtherHoursInfoModel) {
        const item = this.otherHours().find(item => item.tag === info.tag());
        if (item) {
            return item.reason;
        }

        return '';
    }
}

/**
 * Adds some additional formatting for days.
 */
export class DayHoursModel extends HoursModel {
    // Computed observables
    formattedDay: KnockoutComputed<string>;
    dailyTotal: KnockoutComputed<number>;

    constructor(model: any = {}) {
        super(model);

        this.dailyTotal = ko.pureComputed(() => {
            return sum([
                this.clockedHours().asHours(),
                this.vacationHours().hours,
                this.specialLeaveHours().hours,
                this.sickLeaveHours().hours
            ]);
        });

        this.formattedDay = ko.pureComputed(() => this.date().format('ddd DD MMM'));
        this.formattedVacationHours = ko.pureComputed(() =>
            this.formatLeaveHours(this.vacationHours(), 'Vakantie'));
        this.formattedSpecialLeaveHours = ko.pureComputed(() =>
            this.formatLeaveHours(this.specialLeaveHours(), 'Bijzonder verlof'));
        this.formattedSickLeaveHours = ko.pureComputed(() =>
            this.formatLeaveHours(this.sickLeaveHours(), 'Ziek'));
    }
}
